import { FC, useState } from "react";
import { FileText } from "lucide-react";

import { useAppSelector } from "../store";

import { FeedCheckIcon, FeedWarningIcon } from "../ui/icons";
import { MEDIUM_ICON_SIZE_STYLES } from "../ui/styles";
import { SlideOverWrapper } from "../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import { NamedRow } from "../components/GeneralUI/NamedRow/NamedRow";
import { Button } from "../ui/layout/molecules/Button";

import { FeedStatusBadge } from "./FeedStatusBadge";
import { StringifiedImport } from "./types";
import { parseDateToLocalTime } from "../helpers";

const NO_HISTORY_MESSAGE = "No feed history";

const HistoryIcon = (isTotalSuccess: boolean) =>
  isTotalSuccess ? (
    <FeedCheckIcon className={MEDIUM_ICON_SIZE_STYLES} />
  ) : (
    <FeedWarningIcon className={MEDIUM_ICON_SIZE_STYLES} />
  );

const historyEntry = (
  { occurredAt, productsCount, imagesCount, warningsCount }: StringifiedImport,
  index: number,
) => {
  const isImportSuccessful = productsCount > 0 && imagesCount > 0;
  const hasWarnings = warningsCount > 0;
  return (
    <div
      className="border-t border-stone-200 text-left first:border-t-0 first:-mt-6 dark:border-stone-800"
      key={index}
    >
      <div className="flex items-center gap-4 pt-4">
        {HistoryIcon(isImportSuccessful && !hasWarnings)}
        <div>
          <p className="text-primary">
            {isImportSuccessful
              ? "Feed imported successfully"
              : "Products failed to import"}
          </p>
          <p className="text-left text-xs text-stone-500">
            {occurredAt ? parseDateToLocalTime(occurredAt) : "No Date Provided"}
          </p>
          <p className="text-left text-xs text-stone-500">
            {`Total Products: ${productsCount}, Total Images: ${imagesCount}`}
          </p>
        </div>
      </div>
    </div>
  );
};

export const FeedHistory: FC<{ triggerWithButton?: boolean }> = ({
  triggerWithButton,
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const feedHistory = useAppSelector((state) => state.feed.list);

  const hideHistory = () => setShowHistory(false);

  const HistorySlider = (
    <SlideOverWrapper
      open={showHistory}
      onClose={hideHistory}
      title="Import history"
    >
      {feedHistory.length > 0 ? (
        feedHistory.map(historyEntry)
      ) : (
        <p className="border-t-0! p-4 text-left">{NO_HISTORY_MESSAGE}</p>
      )}
    </SlideOverWrapper>
  );

  return triggerWithButton ? (
    <Button
      id="showHistory"
      title="Import History"
      buttonType="primary"
      disabled={!feedHistory.length}
      onClick={() => setShowHistory(true)}
    >
      <>
        <FileText className={`${MEDIUM_ICON_SIZE_STYLES} sm:mr-2`} />
        {HistorySlider}
      </>
    </Button>
  ) : (
    <>
      <NamedRow
        title="Import history"
        editPermissions="site:basic_config:view"
        onClick={
          feedHistory.length > 0 ? () => setShowHistory(true) : undefined
        }
      >
        <FeedStatusBadge />
      </NamedRow>
      {HistorySlider}
    </>
  );
};
